import Alert from "@cloudscape-design/components/alert"
import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import Header from "@cloudscape-design/components/header"
import Select, { type SelectProps } from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import TextFilter from "@cloudscape-design/components/text-filter"
import { useState, useMemo, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { WorkflowStage } from "@/generated/sdk"
import type { DTARecord } from "../../services/dtaApi"
import Pagination from "../../components/Pagination/Pagination"
import { formatDTADateTime, STATUS_COLOR } from "../../components/shared/dtaDisplay"
import { dtaDetailsPath, vendorSubmitPath } from "../../components/shared/vendorRoutes"
import { useAuth } from "../../contexts/AuthContext"
import { useVendorDtasList, useExpandedDtaVersions } from "../../hooks/queries/useVendorDtas"
import { usePageParam } from "../../hooks/usePageParam"
import { filterLatestMinorPerMajor } from "../../utils/versionFilter"
import InsightsPanel from "./InsightsPanel"

const ITEMS_PER_PAGE = 20

const STATUS_OPTIONS: SelectProps.Option[] = [
  { value: "", label: "All Statuses" },
  { value: WorkflowStage.Review, label: "Review" },
  { value: WorkflowStage.Approved, label: "Approved" },
  { value: WorkflowStage.Active, label: "Active" },
  { value: WorkflowStage.Inactive, label: "Inactive" },
]

const hasPreviousVersions = (row: { version: string }) => parseInt(row.version.split(".")[0]) > 1

// Version child rows carry a version's stage, which the SDK under-types as loose `string` and can
// be absent for stage-less versions. Widen the DTARecord's `WorkflowStage` to allow `undefined`
// rather than injecting an empty sentinel that would silently break the status badge lookup.
type Row = Omit<DTARecord, "workflow_stage"> & {
  workflow_stage: WorkflowStage | undefined
  isVersionRow?: boolean
  isLoadingRow?: boolean
}

const rowKey = (row: Row) => {
  if (row.isLoadingRow) return `${row.dta_id}::loading`
  if (row.isVersionRow) return `${row.dta_id}::${row.version}`
  return row.dta_id
}

const VendorDashboard = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const vendorId = user?.isAuthenticated ? user.principalId : undefined

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isRefetching,
    refetch,
  } = useVendorDtasList(vendorId)

  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = searchParams.get("status") ?? ""
  const [searchTerm, setSearchTermRaw] = useState("")
  const [currentPage, setCurrentPage] = usePageParam()
  const [expandedIds, setExpandedIds] = useState<string[]>([])

  // Backed by the URL so the priority tiles (which are links) can drive the filter on-page and the
  // selection survives refresh/sharing. Changing the status resets pagination to page 1.
  const setStatusFilter = (next: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next) params.set("status", next)
        else params.delete("status")
        params.delete("page")
        return params
      },
      { replace: true },
    )
  }
  const setSearchTerm = (next: string) => {
    setSearchTermRaw(next)
    setCurrentPage(1)
  }

  // Auto-fetch remaining pages in the background
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const allDtas = useMemo(() => {
    const items = data?.pages.flatMap((p) => p.items) ?? []
    return [...items].sort(
      (a, b) => new Date(b.last_modified_at).getTime() - new Date(a.last_modified_at).getTime(),
    )
  }, [data])

  const filteredDtas = useMemo(
    () =>
      allDtas.filter((dta) => {
        const matchesStatus = !statusFilter || dta.workflow_stage === statusFilter
        const q = searchTerm.toLowerCase()
        const matchesSearch =
          !searchTerm ||
          dta.study_id.toLowerCase().includes(q) ||
          dta.domains?.some((d) => d.toLowerCase().includes(q))
        return matchesStatus && matchesSearch
      }),
    [allDtas, statusFilter, searchTerm],
  )

  const pageCount = Math.max(1, Math.ceil(filteredDtas.length / ITEMS_PER_PAGE))
  const paginatedDtas = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredDtas.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredDtas, currentPage])

  const versionsByDta = useExpandedDtaVersions(expandedIds)

  const toggleExpand = (dtaId: string) =>
    setExpandedIds((prev) =>
      prev.includes(dtaId) ? prev.filter((id) => id !== dtaId) : [...prev, dtaId],
    )

  const childrenByDta = useMemo(() => {
    const map: Record<string, Row[]> = {}
    for (const dta of paginatedDtas) {
      if (!expandedIds.includes(dta.dta_id)) continue
      const state = versionsByDta[dta.dta_id]
      if (state?.isLoading) {
        map[dta.dta_id] = [{ ...dta, version: "", isLoadingRow: true }]
      } else if (state) {
        map[dta.dta_id] = filterLatestMinorPerMajor(state.versions, dta.version).map((v) => ({
          ...dta,
          version: v.version,
          // The SDK types a version's stage as loose `string`; narrow it to the WorkflowStage it
          // actually is (or `undefined` when the version has no stage) at this boundary.
          workflow_stage: v.workflow_stage as WorkflowStage | undefined,
          last_modified_at: v.created_at,
          isVersionRow: true,
        }))
      }
    }
    return map
  }, [paginatedDtas, expandedIds, versionsByDta])

  const handleRefresh = () => {
    setCurrentPage(1)
    void refetch()
  }

  const canGoNext = currentPage < pageCount
  const showPagination = paginatedDtas.length > 0 && (currentPage > 1 || canGoNext)

  const padded = (content: React.ReactNode) => <Box padding={{ vertical: "xs" }}>{content}</Box>

  const columnDefinitions: TableProps.ColumnDefinition<Row>[] = [
    {
      id: "study",
      header: "Study",
      cell: (row) => padded(row.isVersionRow || row.isLoadingRow ? "" : row.study_id),
    },
    {
      id: "domain",
      header: "Domain",
      cell: (row) => padded(row.isVersionRow || row.isLoadingRow ? "" : row.domains[0] || "-"),
    },
    {
      id: "version",
      header: "Version",
      cell: (row) => padded(row.isLoadingRow ? <Spinner /> : `v${row.version}`),
    },
    {
      id: "status",
      header: "Status",
      cell: (row) =>
        padded(
          row.isLoadingRow || !row.workflow_stage ? (
            ""
          ) : (
            <Badge color={STATUS_COLOR[row.workflow_stage]}>{row.workflow_stage}</Badge>
          ),
        ),
    },
    {
      id: "lastUpdated",
      header: "Last Updated",
      cell: (row) =>
        padded(row.isLoadingRow ? "Loading..." : formatDTADateTime(row.last_modified_at)),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) =>
        padded(
          row.isLoadingRow ? (
            ""
          ) : (
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Button
                variant="inline-link"
                onClick={() => navigate(dtaDetailsPath(row.dta_id, row.version))}
              >
                View DTA
              </Button>
              <Button
                variant="inline-link"
                onClick={() => navigate(vendorSubmitPath(row.dta_id, row.version))}
              >
                Submit Data
              </Button>
            </SpaceBetween>
          ),
        ),
    },
  ]

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Review the DTAs shared with you and submit data to study teams"
        >
          Data Transfer Agreements
        </Header>
      }
    >
      <SpaceBetween size="l">
        {!error && allDtas.length > 0 && (
          <InsightsPanel allDtas={allDtas} loading={isLoading} onStatusSelect={setStatusFilter} />
        )}

        {error ? (
          <Alert
            type="error"
            header="Unable to load DTAs"
            action={<Button onClick={() => void refetch()}>Retry</Button>}
          >
            Error: {error.message}
          </Alert>
        ) : (
          <Container
            header={
              <Header
                variant="h2"
                actions={
                  <Button iconName="refresh" loading={isRefetching} onClick={handleRefresh}>
                    Refresh
                  </Button>
                }
              >
                My DTAs
              </Header>
            }
          >
            <Table
              variant="embedded"
              columnDefinitions={columnDefinitions}
              items={paginatedDtas as Row[]}
              trackBy={rowKey}
              onRowClick={({ detail }) => {
                if (detail.item.isLoadingRow) return
                navigate(dtaDetailsPath(detail.item.dta_id, detail.item.version))
              }}
              loading={isLoading}
              loadingText="Loading DTAs..."
              ariaLabels={{
                tableLabel: "My DTAs",
                expandButtonLabel: (item) => `Show previous versions for ${item.study_id}`,
                collapseButtonLabel: (item) => `Hide previous versions for ${item.study_id}`,
              }}
              filter={
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <div style={{ flexGrow: 1, maxWidth: 480 }}>
                    <TextFilter
                      filteringText={searchTerm}
                      filteringPlaceholder="Search by Study or Domain..."
                      filteringAriaLabel="Search DTAs"
                      onChange={({ detail }) => setSearchTerm(detail.filteringText)}
                    />
                  </div>
                  <div style={{ minWidth: 180 }}>
                    <Select
                      selectedOption={
                        STATUS_OPTIONS.find((o) => o.value === statusFilter) ?? STATUS_OPTIONS[0]
                      }
                      options={STATUS_OPTIONS}
                      ariaLabel="Filter by status"
                      onChange={({ detail }) => setStatusFilter(detail.selectedOption.value ?? "")}
                    />
                  </div>
                </div>
              }
              expandableRows={{
                getItemChildren: (item) => childrenByDta[item.dta_id] ?? [],
                isItemExpandable: (item) =>
                  !item.isVersionRow && !item.isLoadingRow && hasPreviousVersions(item),
                expandedItems: paginatedDtas.filter((d) => expandedIds.includes(d.dta_id)) as Row[],
                onExpandableItemToggle: ({ detail }) => toggleExpand(detail.item.dta_id),
              }}
              empty={
                <Box textAlign="center" color="inherit">
                  {filteredDtas.length === 0 && allDtas.length > 0
                    ? "No DTAs match your filters."
                    : "No DTAs have been shared with you yet."}
                </Box>
              }
              pagination={
                showPagination && (
                  <Pagination
                    currentPage={currentPage}
                    pageCount={pageCount}
                    hasMore={hasNextPage}
                    loading={isLoading}
                    onPageChange={setCurrentPage}
                    onNext={() => canGoNext && setCurrentPage(currentPage + 1)}
                    onPrevious={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                  />
                )
              }
            />
          </Container>
        )}
      </SpaceBetween>
    </ContentLayout>
  )
}

export default VendorDashboard
